import { supabase } from '../lib/supabaseClient';

interface SyncTask {
  id: string;
  type: 'data_fetch' | 'cache_update' | 'user_action' | 'analytics';
  priority: 'low' | 'medium' | 'high';
  payload: any;
  retryCount: number;
  maxRetries: number;
  nextRetry: number;
  status: 'pending' | 'running' | 'completed' | 'failed';
}

class BackgroundSyncService {
  private tasks: SyncTask[] = [];
  private isRunning = false;
  private syncInterval: NodeJS.Timeout | null = null;
  private maxConcurrent = 3;
  private runningTasks = 0;

  constructor() {
    this.startSync();
    this.setupVisibilityHandlers();
  }

  addTask(type: SyncTask['type'], payload: any, priority: SyncTask['priority'] = 'medium'): string {
    const task: SyncTask = {
      id: `${type}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      type,
      priority,
      payload,
      retryCount: 0,
      maxRetries: type === 'analytics' ? 1 : 3,
      nextRetry: Date.now(),
      status: 'pending'
    };

    this.tasks.push(task);
    this.sortTasksByPriority();
    
    if (!this.isRunning) {
      this.processNextTask();
    }

    return task.id;
  }

  private startSync(): void {
    if (this.syncInterval) return;
    
    this.isRunning = true;
    this.syncInterval = setInterval(() => {
      this.processNextTask();
    }, 2000);
  }

  private stopSync(): void {
    if (this.syncInterval) {
      clearInterval(this.syncInterval);
      this.syncInterval = null;
    }
    this.isRunning = false;
  }

  private async processNextTask(): Promise<void> {
    if (this.runningTasks >= this.maxConcurrent) return;

    const task = this.getNextTask();
    if (!task) return;

    this.runningTasks++;
    task.status = 'running';

    try {
      await this.executeTask(task);
      task.status = 'completed';
      this.removeTask(task.id);
    } catch (error) {
      console.warn(`Background sync task failed: ${task.type}`, error);
      await this.handleTaskFailure(task, error);
    } finally {
      this.runningTasks--;
    }
  }

  private async executeTask(task: SyncTask): Promise<void> {
    switch (task.type) {
      case 'data_fetch':
        await this.executeFetchTask(task.payload);
        break;
      case 'cache_update':
        await this.executeCacheTask(task.payload);
        break;
      case 'user_action':
        await this.executeUserActionTask(task.payload);
        break;
      case 'analytics':
        await this.executeAnalyticsTask(task.payload);
        break;
    }
  }

  private async executeFetchTask(payload: any): Promise<void> {
    const { table, query, cacheKey } = payload;
    
    const { data, error } = await supabase
      .from(table)
      .select(query.select || '*')
      .limit(query.limit || 50);

    if (error) throw error;

    // Cache the result
    if (cacheKey && data) {
      localStorage.setItem(`bg_cache_${cacheKey}`, JSON.stringify({
        data,
        timestamp: Date.now(),
        ttl: 300000 // 5 minutes
      }));
    }
  }

  private async executeCacheTask(payload: any): Promise<void> {
    const { action, key, data } = payload;
    
    switch (action) {
      case 'warm':
        // Preload data for key
        break;
      case 'invalidate':
        localStorage.removeItem(`bg_cache_${key}`);
        break;
      case 'update':
        localStorage.setItem(`bg_cache_${key}`, JSON.stringify({
          data,
          timestamp: Date.now(),
          ttl: 300000
        }));
        break;
    }
  }

  private async executeUserActionTask(payload: any): Promise<void> {
    const { action, data } = payload;
    
    // Execute delayed user actions (like form submissions, updates)
    switch (action) {
      case 'form_submit':
        await supabase.from(data.table).insert(data.values);
        break;
      case 'update_record':
        await supabase.from(data.table).update(data.values).eq('id', data.id);
        break;
    }
  }

  private async executeAnalyticsTask(payload: any): Promise<void> {
    try {
      // Local storage'dan analytics verilerini al
      const queue = JSON.parse(localStorage.getItem('analytics_queue') || '[]');
      if (queue.length === 0) return;

      // Batch olarak gönder (max 10 item) ve user_id ekle
      const { data: { user } } = await supabase.auth.getUser();
      const batch = queue.splice(0, 10).map((item: any) => ({
        ...item,
        user_id: user?.id || null,
        session_id: localStorage.getItem('supabase.auth.token') || null
      }));
      
      const { error } = await supabase.from('analytics_events').insert(batch);
      
      if (!error) {
        // Başarılıysa queue'yu güncelle
        localStorage.setItem('analytics_queue', JSON.stringify(queue));
      } else {
        // Hata varsa geri koy (user_id olmadan)
        const originalBatch = batch.map(({ user_id, session_id, ...item }) => item);
        queue.unshift(...originalBatch);
        localStorage.setItem('analytics_queue', JSON.stringify(queue));
        throw error;
      }
    } catch (error: any) {
      // Analytics hatası kritik değil, sessizce devam et
      if (error?.code === 'PGRST106' || error?.message?.includes('404')) {
        // Tablo bulunamadı - analytics'i devre dışı bırak
        return;
      }
      // Diğer hatalar için log
      if (error?.message) {
        console.debug('Analytics sync skipped:', error.message);
      }
    }
  }

  private async handleTaskFailure(task: SyncTask, error: any): Promise<void> {
    task.retryCount++;
    
    if (task.retryCount >= task.maxRetries) {
      task.status = 'failed';
      this.removeTask(task.id);
      
      // Log critical failures
      if (task.priority === 'high') {
        console.error(`Critical background task failed: ${task.type}`, error);
      }
    } else {
      // Exponential backoff
      const delay = Math.min(1000 * Math.pow(2, task.retryCount), 30000);
      task.nextRetry = Date.now() + delay;
      task.status = 'pending';
    }
  }

  private getNextTask(): SyncTask | null {
    const now = Date.now();
    return this.tasks.find(task => 
      task.status === 'pending' && 
      task.nextRetry <= now
    ) || null;
  }

  private sortTasksByPriority(): void {
    const priorityOrder = { high: 3, medium: 2, low: 1 };
    this.tasks.sort((a, b) => {
      if (a.priority !== b.priority) {
        return priorityOrder[b.priority] - priorityOrder[a.priority];
      }
      return a.nextRetry - b.nextRetry;
    });
  }

  private removeTask(taskId: string): void {
    this.tasks = this.tasks.filter(task => task.id !== taskId);
  }

  private setupVisibilityHandlers(): void {
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        // Reduce sync frequency when tab is hidden
        this.stopSync();
        this.syncInterval = setInterval(() => {
          this.processNextTask();
        }, 10000); // Every 10 seconds when hidden
      } else {
        // Resume normal sync when tab is visible
        this.stopSync();
        this.startSync();
      }
    });
  }

  // Public methods
  getTaskStats() {
    return {
      total: this.tasks.length,
      pending: this.tasks.filter(t => t.status === 'pending').length,
      running: this.tasks.filter(t => t.status === 'running').length,
      failed: this.tasks.filter(t => t.status === 'failed').length
    };
  }

  clearCompletedTasks(): void {
    this.tasks = this.tasks.filter(task => task.status !== 'completed');
  }

  pauseSync(): void {
    this.stopSync();
  }

  resumeSync(): void {
    this.startSync();
  }
}

export const backgroundSyncService = new BackgroundSyncService();
export type { SyncTask };
